import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Button, Fieldset, Form, Layout } from '@vwfs-bronson/bronson-react';
import { Formik } from 'formik';
import React, { FunctionComponent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from 'react-redux';
import {
  usedCarFormSelectorPageSliceActions,
  usedCarFormSelectorPageSliceSelectors,
} from '../../screens/NewQuotation/UsedCarFormSelectorPage/UsedCarFormSelectorPage.slice';
import { getMaxFirstRegistrationDate } from '../../services/common/validation';
import { useGetLookupQuery } from '../../services/redux/features/api/lookup.api-slice';
import { useSearchUsedCarMutation } from '../../services/redux/features/api/usedCar.api-slice';
import { DatePickerFormField } from '../Fieldset/DatePickerFormField';
import { NumericInputFormField } from '../Fieldset/NumericInputFormField';
import { RegistrationNumberFormField } from '../Fieldset/RegistrationNumberFormField';
import { SelectFormField } from '../Fieldset/SelectFormField';
import { ChassisNumberFormField, typeSafeFormFieldName } from './UsedCarSelectionFormFieldset';
import { DropdownOption, UsedCarSelectionFormFields } from './UsedCarSelectionFormModels';
import { usedCarSelectionFormValidationSchema } from './UsedCarSelectionFormValidationSchema';

interface UsedCarSelectionFormProps {
  dropdownData?: {
    vehicleClass: Array<DropdownOption>;
    vatApplicable: Array<DropdownOption>;
    brand: Array<DropdownOption>;
    model: Array<DropdownOption>;
    modelYear: Array<DropdownOption>;
    trim: Array<DropdownOption>;
    variant: Array<DropdownOption>;
  };
}

export const UsedCarSelectionForm: FunctionComponent<UsedCarSelectionFormProps> = () => {
  const [searchUsedCar, { isLoading }] = useSearchUsedCarMutation();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data: brandLookups } = useGetLookupQuery({ group: 'usedCar', resource: 'brand' });
  const { data: modelLookups } = useGetLookupQuery({ group: 'usedCar', resource: 'model' });
  const { data: modelYearLookups } = useGetLookupQuery({ group: 'usedCar', resource: 'modelYear' });
  const { data: trimLookups } = useGetLookupQuery({ group: 'usedCar', resource: 'trim' });
  const { data: variantLookups } = useGetLookupQuery({ group: 'usedCar', resource: 'variant' });
  const { data: vatApplicableLookups } = useGetLookupQuery({ group: 'usedCar', resource: 'vatApplicable' });
  const { data: vehicleClassLookups } = useGetLookupQuery({ group: 'usedCar', resource: 'vehicleClass' });

  const handleSearchCar = async (registrationNumber: string): Promise<UsedCarSelectionFormFields> => {
    const usedCarData = await searchUsedCar({ registrationNumber }).unwrap();

    dispatch(usedCarFormSelectorPageSliceActions.updateSearchResults({ data: usedCarData }));

    return usedCarData;
  };

  const initialFormValueFromState = useSelector(usedCarFormSelectorPageSliceSelectors.usedCarSelectionForm).values;

  return (
    <>
      <Formik
        initialValues={initialFormValueFromState}
        validationSchema={usedCarSelectionFormValidationSchema()}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onSubmit={() => {}}
      >
        {({ values, isValid, dirty, isSubmitting, setValues, setFieldError }) => {
          const isRegistrationNumberExisting = !!values.registrationNumber;

          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            dispatch(
              usedCarFormSelectorPageSliceActions.updateForm({
                formName: 'usedCarSelection',
                values,
                isValid,
                dirty,
              })
            );
          }, [values, isValid, dirty]);

          return (
            <Form>
              <Fieldset.Row>
                <Layout>
                  <Layout.Item default="3/4" s="1/1">
                    <RegistrationNumberFormField
                      labelTranslationKey="usedCarSelectionForm:form.formFields.searchField.label"
                      placeholderTranslationKey="usedCarSelectionForm:form.formFields.searchField.placeholder"
                      fieldName={typeSafeFormFieldName('registrationNumber')}
                    />
                  </Layout.Item>

                  <Layout.Item default="1/4" s="1/1">
                    <Fieldset.Row>
                      <Button
                        full
                        testId="usedCarSelectionForm_button_search"
                        type="button"
                        className="u-mt"
                        onClick={async () => {
                          try {
                            dispatch(usedCarFormSelectorPageSliceActions.updateSearchResults({ data: null }));
                            setValues({ ...values, brand: '', model: '', variant: '' });
                            const usedCarData = await handleSearchCar(values.registrationNumber);
                            setValues(usedCarData);
                          } catch (error) {
                            if ((error as FetchBaseQueryError).status === 404) {
                              setFieldError(
                                typeSafeFormFieldName('registrationNumber'),
                                t('usedCarSearchForm:form.formFields.registrationNumber.notFoundError')
                              );
                            }
                          }
                        }}
                        disabled={!isRegistrationNumberExisting || isLoading || isSubmitting}
                      >
                        {t('usedCarSelectionForm:form.searchButton')}
                      </Button>
                    </Fieldset.Row>
                  </Layout.Item>
                </Layout>
              </Fieldset.Row>

              <Fieldset.Row>
                <Layout>
                  <Layout.Item default="1/2" s="1/1">
                    <SelectFormField
                      optionList={vehicleClassLookups ?? []}
                      fieldName="vehicleClass"
                      labelTranslationKey="usedCarSelectionForm:form.formFields.vehicleClass.label"
                      testId="dropdown_vehicleClass"
                    />
                  </Layout.Item>

                  <Layout.Item default="1/2" s="1/1">
                    <SelectFormField
                      optionList={vatApplicableLookups ?? []}
                      fieldName="vatApplicable"
                      labelTranslationKey="usedCarSelectionForm:form.formFields.vatApplicable.label"
                      isDisabled
                      testId="dropdown_vat"
                    />
                  </Layout.Item>
                </Layout>
              </Fieldset.Row>

              <Fieldset.Row>
                <Layout>
                  <Layout.Item default="1/4" s="1/1">
                    <SelectFormField
                      optionList={brandLookups ?? []}
                      fieldName="brand"
                      labelTranslationKey="usedCarSelectionForm:form.formFields.brand.label"
                      testId="dropdown_brand"
                    />
                  </Layout.Item>

                  <Layout.Item default="1/4" s="1/1">
                    <SelectFormField
                      optionList={modelLookups ?? []}
                      fieldName="model"
                      labelTranslationKey="usedCarSelectionForm:form.formFields.model.label"
                      testId="dropdown_model"
                    />
                  </Layout.Item>

                  <Layout.Item default="1/4" s="1/1">
                    <SelectFormField
                      optionList={modelYearLookups ?? []}
                      fieldName="modelYear"
                      labelTranslationKey="usedCarSelectionForm:form.formFields.modelYear.label"
                      testId="dropdown_year"
                    />
                  </Layout.Item>

                  <Layout.Item default="1/4" s="1/1">
                    <SelectFormField
                      optionList={trimLookups ?? []}
                      fieldName="trim"
                      labelTranslationKey="usedCarSelectionForm:form.formFields.trim.label"
                      testId="dropdown_trim"
                    />
                  </Layout.Item>
                </Layout>
              </Fieldset.Row>

              <Fieldset.Row>
                <Layout>
                  <Layout.Item default="1/2">
                    <SelectFormField
                      optionList={variantLookups ?? []}
                      fieldName="variant"
                      labelTranslationKey="usedCarSelectionForm:form.formFields.variant.label"
                      testId="drodpown_variant"
                    />
                  </Layout.Item>
                  <Layout.Item default="1/2">
                    <NumericInputFormField
                      type="money"
                      fieldName="deliveryCost"
                      labelTranslationKey="Delivery Cost"
                      testId="drodpown_variant"
                    />
                  </Layout.Item>
                </Layout>
              </Fieldset.Row>

              <Fieldset.Row>
                <Layout>
                  <Layout.Item default="1/2">
                    <ChassisNumberFormField testId="inputField_chassisNumber" />
                  </Layout.Item>

                  <Layout.Item default="1/2">
                    <DatePickerFormField
                      fieldName={typeSafeFormFieldName('firstRegistration')}
                      labelTranslationKey={t(`usedCarSelectionForm:form.formFields.firstRegistration.label`)}
                      testId="inputField_firstRegistration"
                      maxDate={getMaxFirstRegistrationDate()}
                    />
                  </Layout.Item>
                </Layout>
              </Fieldset.Row>

              <Fieldset.Row>
                <Layout>
                  <Layout.Item default="1/2">
                    <NumericInputFormField
                      type="numeric"
                      fieldName={typeSafeFormFieldName('recordedMileage')}
                      labelTranslationKey={t(`usedCarSelectionForm:form.formFields.recordedMileage.label`)}
                      testId="inputField_recordedMilage"
                    />
                  </Layout.Item>

                  <Layout.Item default="1/2">
                    <NumericInputFormField
                      type="money"
                      fieldName="accessories"
                      labelTranslationKey="usedCarSelectionForm:form.formFields.accessories.label"
                      testId="drodpown_variant"
                    />
                  </Layout.Item>
                </Layout>
              </Fieldset.Row>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
